import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Modal as TheForkModal } from 'app/components/BlockCTA/Modal'
import { Button } from 'app/components/Common/Button'
import { FlashNews, Props as FlashNewsProps } from 'app/components/FlashNews'
import { Logo as Icon } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { theme } from 'app/theme'
import { Media } from 'app/theme/media'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Hamburger } from './Hamburger'
import { Props as LanguageItemProps } from './LanguageItem'
import { Languages } from './Languages'
import { LanguagesMobile } from './LanguagesMobile'
import { Props as MenuItemProps } from './MenuItem'
import { Navigation } from './Navigation'
import { Syncrobox } from './Syncrobox'
import { Toolbar } from './Toolbar'

export interface Props {
  flashNewsProps?: FlashNewsProps
  languagesList: LanguageItemProps[]
  menuItems: MenuItemProps[]
  pageID?: string
  variant?: Variant
}

export const Header = memo(function Header({
  flashNewsProps,
  languagesList,
  menuItems,
}: Props) {
  const {
    beachCTA,
    bookingID,
    languageCode,
    languagePrefix,
    quotationPageURL,
    siteName,
    syncroStatus,
    variant,
  } = useContext(HeaderContext) as any

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)
  const [modalIBEStatus, setModalIBEStatus] = useState(false)
  const [modalTheForkStatus, setModalTheForkStatus] = useState(false)
  const [scroll, setScroll] = useState(false)

  const endTrap = useRef<any>(null)

  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  const quotationRealPageURL = languagePrefix
    ? `/${languagePrefix}/${quotationPageURL}`
    : `/${quotationPageURL}`

  useEffect(() => {
    const onScroll = () => {
      setScroll(window.scrollY > 180)
    }

    window.addEventListener('scroll', onScroll)

    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'Escape':
          setLocked(false)
          setModalStatus(false)
          setModalIBEStatus(false)
          setModalTheForkStatus(false)
          break
      }
    })

    if (endTrap.current) {
      endTrap.current.addEventListener('focus', () => {
        setLocked(false)
        setModalStatus(false)
        setModalIBEStatus(false)
        setModalTheForkStatus(false)
      })
    }

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <Container variant={variant}>
      {flashNewsProps ? <FlashNews {...flashNewsProps} /> : null}

      <Head
        className={`${scroll ? 'fixed' : ''}${
          variant === 'compact' ? ' compact' : ''
        }${isAnimObjectVisible ? ' visible' : ''}`}
        dial={4}
        ref={animObjectRef}
        row
      >
        <Hamburger
          isSticky={scroll || variant === 'compact'}
          languageCode={languageCode}
          onClick={() => {
            setModalStatus(true)
            setLocked(true)
          }}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter') {
              setModalStatus(true)
              setLocked(true)
            }
          }}
        />

        <Media greaterThanOrEqual="desktopSmall">
          <Languages
            isSticky={scroll || variant === 'compact'}
            languagesList={languagesList}
            languagePrefix={languagePrefix}
          />
        </Media>

        <Logo
          aria-label={siteName}
          to={languagePrefix ? `/${languagePrefix}` : '/'}
        >
          <Icon />
        </Logo>

        <Media className="right-side" lessThan="desktopSmall">
          <LanguagesMobile
            isSticky={scroll || variant === 'compact'}
            languagesList={languagesList}
            languagePrefix={languagePrefix}
          />

          {bookingID ? (
            <TheFork
              label={useVocabularyData('book-a-table', languageCode)}
              onClick={() => {
                setLocked(true)
                setModalTheForkStatus(true)
              }}
            />
          ) : null}

          {beachCTA ? (
            <TheFork
              label={useVocabularyData('book-beach', languageCode)}
              role="button"
              target="_blank"
              URL={beachCTA}
            />
          ) : null}
        </Media>

        <Media className="right-side" greaterThanOrEqual="desktopSmall">
          {quotationPageURL ? (
            <QuotationCTA
              label={useVocabularyData('ask', languageCode)}
              URL={quotationRealPageURL}
              role="button"
              variant={scroll || variant === 'compact' ? 'flat' : 'flat-invert'}
            />
          ) : null}

          {bookingID ? (
            <TheFork
              label={useVocabularyData('book-a-table', languageCode)}
              onClick={() => {
                setLocked(true)
                setModalTheForkStatus(true)
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  setLocked(true)
                  setModalTheForkStatus(true)
                }
              }}
              role="button"
              variant={scroll || variant === 'compact' ? 'default' : 'invert'}
            />
          ) : null}

          {beachCTA ? (
            <TheFork
              label={useVocabularyData('book-beach', languageCode)}
              role="button"
              target="_blank"
              URL={beachCTA}
              variant={scroll || variant === 'compact' ? 'default' : 'invert'}
            />
          ) : null}

          <BookingCTA
            label={useVocabularyData('book', languageCode)}
            onClick={() => {
              setLocked(true)
              setModalIBEStatus(true)
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                setLocked(true)
                setModalIBEStatus(true)
              }
            }}
            role="button"
            variant={scroll || variant === 'compact' ? 'default' : 'invert'}
          />
        </Media>
      </Head>

      <Modal
        aria-hidden={!modalStatus}
        aria-labelledby="Navigation"
        className={modalStatus ? 'open' : undefined}
        role="dialog"
      >
        <Navigation
          languageCode={languageCode}
          menuItems={menuItems}
          modalStatus={modalStatus}
          onClick={() => {
            setModalStatus(false)
            setLocked(false)
          }}
        />

        <EndTrap ref={endTrap} tabIndex={0} />
      </Modal>

      <Modal
        aria-hidden={!modalIBEStatus}
        aria-labelledby="Booking Engine"
        className={modalIBEStatus ? 'open' : undefined}
        role="dialog"
      >
        <Syncrobox
          modalStatus={modalIBEStatus}
          onClick={() => {
            setLocked(false)
            setModalIBEStatus(false)
          }}
          syncroStatus={syncroStatus}
        />
        <EndTrap ref={endTrap} tabIndex={0} />
      </Modal>

      {bookingID ? (
        <TheForkModal
          ID={bookingID}
          modalStatus={modalTheForkStatus}
          onClick={() => {
            setLocked(false)
            setModalTheForkStatus(false)
          }}
        />
      ) : null}

      <Media lessThan="desktopSmall">
        <Toolbar quotationURL={quotationRealPageURL} />
      </Media>
    </Container>
  )
})

const Logo = styled(Link)`
  opacity: 0;
  position: absolute;
  top: 1.5rem;
  left: 50%;
  transform: translate(-50%, -2.5rem);
  transition:
    opacity 1s 1.1s ease-in-out,
    transform 1s 1.1s ease-in-out;

  svg {
    width: 9.3125rem;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralLight1};
  }

  @media (max-width: 1199px) {
    top: 1rem;
    opacity: 1 !important;
    transform: translateX(-50%) !important;
    transition: none !important;
  }

  svg {
    width: 7.0625rem;
  }
`

const QuotationCTA = styled(Button)`
  margin-right: 1.5rem;
  opacity: 0;
  transform: translateY(-2.5rem);
  transition:
    opacity 1s 1.4s ease-in-out,
    transform 1s 1.4s ease-in-out;
`

const TheFork = styled(Button)`
  margin-right: 0.375rem;
  opacity: 0;
  transform: translateY(-2.5rem);
  transition:
    opacity 1s 1.6s ease-in-out,
    transform 1s 1.6s ease-in-out;

  @media (max-width: 1199px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 3.5rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight2};
    border: 0;
    box-shadow: ${rgba(theme.colors.variants.neutralDark1, 0.05)} 0px -6px 6px;
    padding: 0;
    position: fixed;
    bottom: 3.5rem;
    left: 0;
    margin: 0;
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    z-index: 21;
    &:hover {
      color: ${({ theme }) => theme.colors.variants.primaryDark};
      &:before {
        display: none;
      }
    }
  }
`

const BookingCTA = styled(Button)`
  opacity: 0;
  transform: translateY(-2.5rem);
  transition:
    opacity 1s 1.7s ease-in-out,
    transform 1s 1.7s ease-in-out;
`

const HeaderFixedStyle = css`
  background: ${theme.colors.variants.neutralLight2};
  padding-top: 1.1875rem;
  padding-bottom: 1.1875rem;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 21;

  ${Logo} {
    top: 0.9375rem;
    opacity: 1 !important;
    transition: none !important;

    svg {
      fill: ${theme.colors.variants.primaryDark};
    }
  }

  ${QuotationCTA},
  ${TheFork},
  ${BookingCTA} {
    transition:
      opacity 0s,
      transform 0s;
  }

  @media (max-width: 1199px) {
    height: 4rem;
    padding: 0 1rem;
  }
`

const Head = styled(FlexBox)`
  width: 100%;
  padding: 1.75rem 1.5rem 0;
  position: relative;
  top: 0;
  &.fixed {
    animation: fadeInTop 0.3s;

    ${HeaderFixedStyle}
  }
  &.compact {
    animation: none !important;

    ${HeaderFixedStyle}
  }
  &.visible {
    ${Logo} {
      opacity: 1;
      transform: translateX(-50%);
    }
    ${QuotationCTA},
    ${TheFork},
    ${BookingCTA} {
      opacity: 1;
      transform: none;
    }
  }

  .right-side {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  @keyframes fadeInTop {
    0% {
      top: -4.5rem;
    }
    100% {
      top: 0;
    }
  }

  @media (max-width: 1199px) {
    padding: 1.4375rem 1rem;
  }
`

const Container = styled.header<ContainerProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: 1000;
  &.open {
    pointer-events: unset;
    visibility: visible;
  }
`

const EndTrap = styled.div``

interface ContainerProps {
  variant: Variant
}

export type SyncroStatus = 'default' | 'open'

export type Variant = 'default' | 'compact'
